import React, { createContext, useContext } from 'react';

import { FileUploadS3 } from 'hooks/mutations/server/use-file-upload-s3';
import { noop } from 'utils/helpers';

import { StoryImageBlockProps } from 'types/Story';

type ContentBlockItemState = 'INITIAL' | 'UPLOADING';
type ContentBlockItemContext = 'SINGLE' | 'MULTI';

type ContentBlockItemConfig = {
  item: StoryImageBlockProps;
  state: ContentBlockItemState;
  updateItem(item: StoryImageBlockProps): void;
  removeItem(): void;
  setItemState(state: ContentBlockItemState): void;
  populateItem(options: {
    item: StoryImageBlockProps;
    upload: FileUploadS3;
  }): void;
  context: ContentBlockItemContext;
};

const ContentItemContext = createContext<ContentBlockItemConfig>({
  item: {
    id: 'initial-id',
    state: 'EMPTY',
    caption: '',
  },
  state: 'INITIAL',
  updateItem: noop,
  removeItem: noop,
  populateItem: noop,
  setItemState: noop,
  context: 'SINGLE',
});

export const useContentItemImageContext = () => useContext(ContentItemContext);

function ContentItemContextProvider(props: {
  item: StoryImageBlockProps;
  children: React.ReactNode;
  updateItem(item: StoryImageBlockProps): void;
  removeItem(): void;
  context: ContentBlockItemContext;
}) {
  const { item, updateItem, removeItem, context } = props;

  const [itemState, setItemState] =
    React.useState<ContentBlockItemState>('INITIAL');

  return (
    <ContentItemContext.Provider
      value={{
        item,
        context,
        state: itemState,
        setItemState,
        updateItem,
        removeItem,
        populateItem: (options) => {
          updateItem({
            id: options.item.id,
            state: 'POPULATED',
            base64Preview: options.upload.previewBase64,
            caption: options.item.caption,
            img: {
              src: options.upload.url,
            },
          });
          setItemState('INITIAL');
        },
      }}
    >
      {props.children}
    </ContentItemContext.Provider>
  );
}

export const ContentBlockItemImage = {
  Provider: ContentItemContextProvider,
};
